import { useState, useEffect } from "react";
import { Container } from "@/components/container";
import { Toolbar, ToolbarHeading } from "@/layouts/demo1/toolbar";
import { AgentChatPreview } from "./agent-chat/AgentChatPreview";
import { AgentBuilderForm } from "./agent-form/AgentBuilderForm";
import { FormattedMessage } from "react-intl";
import { Splitter } from "antd";
import { useAuth } from "@/hooks/useAuth";
import { useGetAgent } from "@/hooks/useAgent";

const DEFAULT_FILL = "#F2F2F2";
const DEFAULT_STROKE = "#50B553";
const DEFAULT_SUGGEST = "#0B660E";
const DEFAULT_SUGGEST_OVERLAY = "#ECF8EC";
const DEFAULT_BUTTON = "#50B553";
const DEFAULT_BUTTON_OVERLAY = "#ECF8EC";
const DEFAULT_FONT = "Tahoma";
const DEFAULT_FONT_STYLING = "Normal";
const DEFAULT_FONT_SIZE = "16";
const DEFAULT_HEART = false;
const DEFAULT_DISLIKE = false;
const DEFAULT_COPY = false;

function shallowEqual(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
}

// Helper: convert state to snake_case object for localStorage/compare
function toSnakeCaseData(formData, settings, introImageSettings) {
  return {
    name: formData.name,
    introduction: formData.introduction,
    greeting: formData.greeting,
    fill: settings.fill,
    stroke: settings.stroke,
    font: settings.font,
    font_styling: settings.font_styling,
    font_size: settings.font_size,
    heart: settings.heart ? 1 : 0,
    dislike: settings.dislike ? 1 : 0,
    copy: settings.copy ? 1 : 0,
    button_color: settings.button,
    button_overlay: settings.buttonOverlay,
    suggest_color: settings.suggest,
    suggest_overlay: settings.suggestOverlay,
    intro_image_state: introImageSettings?.enableIntroImage ? 1 : 0, // Thêm dòng này
  };
}

const AgentBuilderPage = () => {
  const { userData } = useAuth();
  const { data: agent } = useGetAgent(userData?.uID);

  const [formData, setFormData] = useState({
    name: "",
    introduction: "",
    greeting: "",
  });

  const [settings, setSettings] = useState({
    fill: DEFAULT_FILL,
    stroke: DEFAULT_STROKE,
    font: DEFAULT_FONT,
    font_styling: DEFAULT_FONT_STYLING,
    font_size: DEFAULT_FONT_SIZE,
    heart: DEFAULT_HEART,
    dislike: DEFAULT_DISLIKE,
    copy: DEFAULT_COPY,
    button: DEFAULT_BUTTON,
    buttonOverlay: DEFAULT_BUTTON_OVERLAY,
    suggest: DEFAULT_SUGGEST,
    suggestOverlay: DEFAULT_SUGGEST_OVERLAY,
  });

  // Baseline để so sánh hasChanges (được set từ agent)
  const [baseline, setBaseline] = useState(null);

  const [hasChanges, setHasChanges] = useState(false);
  const [introImageSettings, setIntroImageSettings] = useState({
    enableIntroImage: false,
  });

  // Map dữ liệu agent -> formData & settings
  const mapAgentToForm = (agentData) => ({
    name: agentData?.name ?? "",
    introduction: agentData?.introduction ?? "",
    greeting: agentData?.greeting ?? "",
  });

  const mapAgentToSettings = (agentData) => ({
    fill: agentData?.fill ?? DEFAULT_FILL,
    stroke: agentData?.stroke ?? DEFAULT_STROKE,
    font: agentData?.font ?? DEFAULT_FONT,
    font_styling: agentData?.font_styling ?? DEFAULT_FONT_STYLING,
    // lưu dạng string cho Select
    font_size: String(agentData?.font_size ?? DEFAULT_FONT_SIZE),
    heart: !!agentData?.heart,
    dislike: !!agentData?.dislike,
    copy: !!agentData?.copy,
    button: agentData?.button_color ?? DEFAULT_BUTTON,
    buttonOverlay: agentData?.button_overlay ?? DEFAULT_BUTTON_OVERLAY,
    suggest: agentData?.suggest_color ?? DEFAULT_SUGGEST,
    suggestOverlay: agentData?.suggest_overlay ?? DEFAULT_SUGGEST_OVERLAY,
  });

  // Hydrate từ agent khi có dữ liệu
  useEffect(() => {
    const a = agent?.data;
    if (!a) return;
    const nextForm = mapAgentToForm(a);
    const nextSettings = mapAgentToSettings(a);
    const nextIntro = { enableIntroImage: a?.intro_image_state ? true : false };
    setFormData(nextForm);
    setSettings(nextSettings);
    setIntroImageSettings(nextIntro);
    const baseComparable = toSnakeCaseData(nextForm, nextSettings, nextIntro);
    setBaseline(baseComparable);
    setHasChanges(false);
  }, [agent]);

  // So sánh dữ liệu hiện tại với baseline (từ agent) để xác định hasChanges
  // Chỉ tính hasChanges dựa trên formData (name, introduction, greeting)
  useEffect(() => {
    if (!baseline) return;
    const currentFormData = {
      name: formData.name,
      introduction: formData.introduction,
      greeting: formData.greeting,
    };
    const baselineFormData = {
      name: baseline.name,
      introduction: baseline.introduction,
      greeting: baseline.greeting,
    };
    setHasChanges(!shallowEqual(currentFormData, baselineFormData));
  }, [formData, baseline]);

  // useEffect(() => {
  //   const handleBeforeUnload = (e) => {
  //     if (hasChanges) {
  //       e.preventDefault();
  //       e.returnValue = '';
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);
  //   return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  // }, [hasChanges]);

  const handleFormChange = (newData) => {
    setFormData(newData);
  };

  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
  };

  const handleIntroImageChange = (data) => {
    setIntroImageSettings(data);
  };

  const handleSave = () => {
    // Sau khi API update thành công (AgentBuilder sẽ gọi), reset hasChanges cho form data.
    const currentFormData = {
      name: formData.name,
      introduction: formData.introduction,
      greeting: formData.greeting,
    };
    const newBaseline = { ...baseline, ...currentFormData };
    setBaseline(newBaseline);
    setHasChanges(false);
  };

  return (
    <>
      <Toolbar>
        <Container>
          <ToolbarHeading
            title={<FormattedMessage id="AGENTS.HOME.TITLE_AGENT" />}
          />
        </Container>
      </Toolbar>
      <Container className="bg-gray-200 p-0">
        <Splitter>
          <Splitter.Panel
            defaultSize="70%"
            min="1%"
            max="99%"
            className="min-h-[calc(100vh-100px)] max-h-[calc(100vh-100px)]"
          >
            <div className="mx-auto py-4 w-3/4 min-w-[400px] px-7 flex justify-center min-h-[calc(100vh-100px)] max-h-[calc(100vh-100px)]">
              <AgentChatPreview
                name={formData.name}
                introduction={formData.introduction}
                greeting={formData.greeting}
                {...settings}
              />
            </div>
          </Splitter.Panel>
          {/* <Splitter.Panel defaultSize="30%" min={300} max="99%"> */}
          <Splitter.Panel defaultSize="340" min="1%" max="99%">
            <div className="w-full">
              <AgentBuilderForm
                onFormChange={handleFormChange}
                initialData={formData}
                settings={settings}
                onSettingsChange={handleSettingsChange}
                onSave={handleSave}
                hasChanges={hasChanges}
                introImageSettings={introImageSettings}
                onIntroImageChange={handleIntroImageChange}
              />
            </div>
          </Splitter.Panel>
        </Splitter>
        {/* <div className="flex flex-row 2xl:grid 2xl:grid-cols-10 min-h-[calc(100vh-100px)] max-h-[calc(100vh-100px)]"> */}

        {/* </div> */}
      </Container>
    </>
  );
};

export { AgentBuilderPage };
